import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProteinMassCalculator = () => {
  const [sequence, setSequence] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Amino acid molecular weights (in g/mol)
  const aminoAcidWeights = {
    'A': 89.1,  // Alanine
    'R': 174.2, // Arginine
    'N': 132.1, // Asparagine
    'D': 133.1, // Aspartic acid
    'C': 121.2, // Cysteine
    'E': 147.1, // Glutamic acid
    'Q': 146.2, // Glutamine
    'G': 75.1,  // Glycine
    'H': 155.2, // Histidine
    'I': 131.2, // Isoleucine
    'L': 131.2, // Leucine
    'K': 146.2, // Lysine
    'M': 149.2, // Methionine
    'F': 165.2, // Phenylalanine
    'P': 115.1, // Proline
    'S': 105.1, // Serine
    'T': 119.1, // Threonine
    'W': 204.2, // Tryptophan
    'Y': 181.2, // Tyrosine
    'V': 117.1  // Valine
  };

  const handleInputChange = (e) => {
    setSequence(e.target.value.toUpperCase());
    setError('');
  };

  const calculateMass = () => {
    if (!sequence.trim()) {
      setError('Please enter an amino acid sequence');
      return;
    }

    const sequenceArray = sequence.trim().toUpperCase().split('');
    let totalMass = 0;
    const aminoAcidCounts = {};
    const invalidAminoAcids = [];
    const calculationSteps = [];
  
    // Validate sequence first
    const invalidChars = sequenceArray.filter(aa => !aminoAcidWeights[aa]);
    if (invalidChars.length > 0) {
      const uniqueInvalid = [...new Set(invalidChars)];
      setError(`Invalid amino acids found: ${uniqueInvalid.join(', ')}. Please use only standard one-letter codes: ${Object.keys(aminoAcidWeights).join(', ')}`);
      setResults(null);
      setChartData(null);
      return;
    }
  
    // Continue with calculation if sequence is valid
    calculationSteps.push('Step 1: Calculating individual amino acid masses');
    const individualMasses = [];
    for (let aa of sequenceArray) {
      if (aminoAcidWeights[aa]) {
        totalMass += aminoAcidWeights[aa];
        aminoAcidCounts[aa] = (aminoAcidCounts[aa] || 0) + 1;
        individualMasses.push(`${aa}: ${aminoAcidWeights[aa]} g/mol`);
      } else {
        invalidAminoAcids.push(aa);
      }
    }
    calculationSteps.push(`Individual amino acid masses:\n${individualMasses.join('\n')}`);
    calculationSteps.push(`Sum of amino acid masses: ${totalMass.toFixed(2)} g/mol`);

    if (invalidAminoAcids.length > 0) {
      setError(`Invalid amino acids found: ${invalidAminoAcids.join(', ')}`);
      return;
    }

    // Add water mass for peptide bonds (one water molecule is lost per peptide bond)
    calculationSteps.push('\nStep 2: Calculating water loss from peptide bond formation');
    const waterMass = 18.015; // g/mol
    const peptideBonds = sequenceArray.length - 1;
    calculationSteps.push(`Number of peptide bonds: ${peptideBonds}`);
    calculationSteps.push(`Water mass per peptide bond: ${waterMass} g/mol`);
    calculationSteps.push(`Total water mass lost: ${(waterMass * peptideBonds).toFixed(2)} g/mol`);

    const finalMass = totalMass - (waterMass * peptideBonds);
    calculationSteps.push('\nStep 3: Calculating final protein mass');
    calculationSteps.push(`Final mass = Sum of amino acid masses - (water mass × number of peptide bonds)`);
    calculationSteps.push(`Final mass = ${totalMass.toFixed(2)} - (${waterMass} × ${peptideBonds})`);
    calculationSteps.push(`Final mass = ${finalMass.toFixed(2)} g/mol`);

    const newResults = {
      totalMass: finalMass.toFixed(2),
      sequenceLength: sequenceArray.length,
      aminoAcidComposition: aminoAcidCounts,
      calculationSteps: calculationSteps
    };

    setResults(newResults);

    // Update chart data
    const sortedCounts = Object.entries(aminoAcidCounts).sort((a, b) => b[1] - a[1]);
    setChartData({
      labels: sortedCounts.map(([aa]) => aa),
      datasets: [{
        label: 'Amino Acid Composition',
        data: sortedCounts.map(([, count]) => count),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    });
  };

  const exampleData = {
    sequence: 'MAEGEITTFTA',
    mass: 3678.23,
    length: 34,
    composition: {
      'A': 2,
      'E': 3,
      'F': 2,
      'G': 2,
      'I': 1,
      'K': 3,
      'L': 3,
      'M': 1,
      'N': 2,
      'P': 2,
      'S': 1,
      'T': 3,
      'Y': 1
    }
  };

  return (
    <>
      <Helmet>
        <title>Protein Mass Calculator | Biology Tools</title>
        <meta name="description" content="Calculate protein molecular mass from amino acid sequence. Features include peptide bond calculations and amino acid composition analysis." />
        <meta property="og:title" content="Protein Mass Calculator" />
        <meta property="og:description" content="Calculate protein molecular mass from amino acid sequence with detailed analysis." />
        <meta property="og:image" content="/images/protein-calculator-preview.jpg" />
        <meta property="og:url" content="https://yourdomain.com/biology/protein-mass" />
        <title>Protein Mass Calculator | Biology Tools</title>
        <meta name="description" content="Calculate protein molecular mass from amino acid sequence. Features include peptide bond calculations and amino acid composition analysis." />
        <meta name="keywords" content="protein mass calculator, molecular weight, amino acids, peptide bonds, protein analysis" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Protein Mass Calculator" />
        <meta property="og:description" content="Calculate protein molecular mass from amino acid sequence with detailed analysis." />
        <meta property="og:image" content="/images/protein-calculator-preview.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Protein Mass Calculator" />
        <meta name="twitter:description" content="Calculate protein molecular mass from amino acid sequence with detailed analysis." />
        <meta name="twitter:image" content="/images/protein-calculator-preview.jpg" />
        
        {/* Additional metadata */}
        <meta name="application-name" content="Biology Calculator Tools" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Protein Calculator" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#4CAF50" />
      </Helmet>

      <div className="w-full min-h-screen p-4 md:p-8 bg-gray-50">
        {/* Header section with image */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <img 
              src="/images/biology/dna-icon.svg" 
              alt="DNA Helix Icon" 
              className="w-12 h-12 md:w-16 md:h-16"
              loading="lazy"
            />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Protein Mass Calculator
              </h1>
              <p className="text-base md:text-lg text-blue-600 font-semibold">
                Population Genetics Analysis
              </p>
            </div>
          </div>
        </div>

        {/* Main content section with image */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4">
            <img
              src="/images/biology/protein-mass-visual.svg"
              alt="Protein Mass Visualization"
              className="w-24 h-24 object-contain"
              loading="lazy"
            />
            <p className="text-sm md:text-base text-gray-700">
              Calculate the molecular mass of a protein based on its amino acid sequence. 
              Enter the one-letter codes for amino acids (e.g., MAEGTK). The calculator 
              accounts for water loss in peptide bond formation.
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto p-4 md:p-8 bg-white rounded-xl shadow-lg"
        >
          <div className="mb-6">
            <label htmlFor="sequence" className="block text-gray-700 font-medium mb-2">
              Amino Acid Sequence:
            </label>
            <textarea
              id="sequence"
              name="sequence"
              value={sequence}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] text-base md:text-lg resize-y"
              placeholder="Enter amino acid sequence (e.g., MAEGTK)"
            />
          </div>

          <button
            onClick={calculateMass}
            className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors duration-200 text-base md:text-lg"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 text-center text-red-500">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2">Molecular Mass</h4>
                  <p className="text-gray-700">{results.totalMass} g/mol</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2">Sequence Length</h4>
                  <p className="text-gray-700">{results.sequenceLength} amino acids</p>
                </div>
              </div>

              {chartData && (
                <div className="mt-8">
                  <div className="h-[300px] md:h-[400px]">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Count'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Amino Acid'
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-3">
                  {results.calculationSteps.map((step, index) => (
                    <pre key={index} className="whitespace-pre-wrap font-mono text-sm md:text-base text-gray-700 p-3 bg-white rounded-lg shadow-sm">
                      {step}
                    </pre>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Example Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-4">Example Calculation 📝</h2>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold mb-4">Sample Protein Data:</h4>
            <ul className="space-y-2 mb-6">
              <li>Sequence: {exampleData.sequence}</li>
              <li>Length: {exampleData.length} amino acids</li>
              <li>Molecular Mass: {exampleData.mass} g/mol</li>
            </ul>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(exampleData.composition).map(([aa, count]) => (
                <div key={aa} className="p-2 bg-gray-50 rounded text-center">
                  {aa}: {count}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProteinMassCalculator;